/* System defined core library */
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable, Observer, Subject, interval, throwError } from 'rxjs';
import { switchMap, takeUntil, map, mergeMap, catchError } from 'rxjs/operators';

/* User defined library/components */
import { HttpService } from '../../../core/http.service';
import { BootstrapParams } from '../../../../bootstrap-params';

/* User defined models */
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { CreatePaymentSessionRequest, CreatePaymentSessionResponse, PaymentOrchestratorRequest, PaymentSession } from '../../../shared/models/scheduling/paymentSession.model';
import { QrRefund, QrRefundStage, QrRefundStatusUpdate, QrRefundStatusUpdateObservable } from '../../../shared/models/scheduling/qr.model';

/* User defined enums/constants */
import { CardFee, QrStatus, LogLevel, PaymentOrchestratorPaymentStatus } from '../../../shared/enums/scheduling.enum';
import { MetricApiService } from './metric-api.service';

@Injectable()
export class PaymentApiService {

  public stopPolling = new Subject();
  public paymentOrchestratorRetryCounter: number = 0;

  constructor(
    private bootstrapParams: BootstrapParams,
    private httpService: HttpService,
    private metricsApiService: MetricApiService
  ) {
    this.stopPolling.subscribe(
      (holdId) => {
        this.metricsApiService.log(LogLevel.Info, `Payment verification polling stopped for \nHold: ${holdId}`);
      });
  }

  private getResultArray(apiResponse: any): any[] {
    let resultArray = apiResponse.results;
    return resultArray || [];
  }

  private getSingleResult(apiResponse: any): any[] {
    let resultArray = apiResponse.results;
    return resultArray.slots;
  }

  private getFirstOrDefault(apiResponse: any): any {
    let resultArray: any = (apiResponse.results !== undefined) ? apiResponse.results : apiResponse;
    return resultArray;
  }
  
  /* Credit card/voucher payment methods */
  // Settle Payment Http Post Method :
  public settlePayment(card: any, flowType: string, transactionId: string, examDetailsForSettlement: any): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      let requestSettlementData: any = {
        "card": card,
        "sessions": examDetailsForSettlement,
        "type": flowType,
        //"AuthorizationStatus": "1",
        "transactionId": transactionId,
        //"Status": "Pending"
      }
      this.httpService.post(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/payments/', requestSettlementData)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((rNArray) => {
          observer.next(rNArray);
          observer.complete();
        })
    })
  }

  // Roll back transaction in case of failure
  public rollBackTransaction(transactionId: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.delete(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/authorizations/?transactionId=' + transactionId)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((rNArray) => {
          if (rNArray != undefined && rNArray != null) {
            observer.next(rNArray);
            observer.complete();
          }
        });
    });
  }

  public rollBackSessionTransaction(paymentOrchrestratorId: string, amount: number): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      // Check if transaction is associated with an appointment
      this.transactionUsed(paymentOrchrestratorId).subscribe((usedResponse) => {
        if (usedResponse.status) {
          this.metricsApiService.log(LogLevel.Info, `Transaction is in use. Rollback not performed.`);
          observer.error('Transaction Used');
          observer.complete();
        } else {
          this.metricsApiService.log(LogLevel.Info, `Attempting to rollback payment transaction`);

          let requestBody = { "amount": amount }
          this.httpService.post(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/payment/' + paymentOrchrestratorId + '/refund', requestBody)
            .pipe(map(this.getFirstOrDefault))
            .subscribe({
              next: (rNArray) => {
                this.httpService.post(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/payments/' + paymentOrchrestratorId + '/unstage', {})
                  .pipe(map(this.getFirstOrDefault))
                  .subscribe({
                    next: (unstageResponse) => {
                      this.metricsApiService.log(LogLevel.Info, `Payment transaction rollback completed.`);
                      this.paymentOrchestratorRetryCounter = 0;
                      observer.next(rNArray);
                      observer.complete();
                    },
                    error: (err) => {
                      this.metricsApiService.log(LogLevel.Error, `Transaction refund completed, but an error occurred in unstaging payment: ${JSON.stringify(err)}`);
                      this.paymentOrchestratorRetryCounter = 0;
                      observer.error(err);
                      observer.complete();
                    }
                  });
              },
              error: (err) => {
                if (err.status === 429) {
                  if (this.paymentOrchestratorRetryCounter < this.bootstrapParams.paymentOrchestratorMaxRetries) {
                    this.paymentOrchestratorRetryCounter++;

                    this.metricsApiService.log(LogLevel.Info, `Received 429 response from payment orchestrator. Retrying ${this.paymentOrchestratorRetryCounter} of ${this.bootstrapParams.paymentOrchestratorMaxRetries}.`);
                    setTimeout(() => {
                      this.rollBackSessionTransaction(paymentOrchrestratorId, amount).subscribe({
                        next: (resp) => {
                          observer.next(resp);
                          observer.complete();
                        },
                        error: (err: any) => {
                          observer.error(err);
                          observer.complete();
                        }
                      });
                    }, this.bootstrapParams.paymentOrchestratorRetryTime);
                  } else {
                    this.metricsApiService.log(LogLevel.Error, `Received 429 response from payment orchestrator. Max retries exceeded.`);
                    this.paymentOrchestratorRetryCounter = 0;
                    observer.error('Orchestrator unreachable');
                    observer.complete();
                  }
                } else {
                  this.metricsApiService.log(LogLevel.Error, `Error rolling back payment transaction: ${JSON.stringify(err)}`);
                  this.paymentOrchestratorRetryCounter = 0;
                  observer.error(err);
                  observer.complete();
                }
              }
            });
        }
      });
    });
  }

  // Valid Card Details
  public validateCardDetails(cardDetails: any): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.post(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/authorizations', cardDetails)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (rNArray) => {
            if (rNArray != undefined && rNArray != null) {
              observer.next(rNArray);
              observer.complete();
            }
          },
          error: (err) => {
            let resp = { message: "Unable to reschedule appointment, Please try later.", status: false, confirmationNumber: "Error" };
            observer.next(resp);
            observer.complete();
          }
        });
    });
  }

  // Valid Voucher Details
  public validateVoucherDetails(voucherDetail: any, transactionId: string): Observable<any> {
    var id = transactionId || ''
    return new Observable((observer: Observer<any>) => {
      this.httpService.post(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/validations/' + id, voucherDetail)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (rNArray) => {
            if (rNArray != undefined && rNArray != null) {
              observer.next(rNArray);
              observer.complete();
            }
          },
          error: (err) => {
            let resp = { message: "Unable to validate Voucher, Please try later.", status: false, confirmationNumber: "Error" };
            observer.next(resp);
            observer.complete();
          }
        });
    });
  }

  // Post Refund
  public postRefundAmount(details: any): Observable<any> {
    return new Observable((observer: any) => {
      this.httpService.post(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/payments/refunds', details)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (rNArray) => {
            let resp = { status: true, transactionId: rNArray.transactionId };
            if (rNArray != undefined && rNArray != null) {
              observer.next(resp);
              observer.complete();
            }
          },
          error: (err) => {
            let resp = { message: "Unable to process refund now, Please try later.", status: false, confirmationNumber: "Error", transactionId: null as string };
            observer.next(resp);
            observer.complete();
          }
        });
    })
  }

  /* Qr(Alipay/WeChat) methods */
  // Refund QR payment
  public refundQRPayment(transactionId: string, refund: QrRefund): Observable<QrRefundStatusUpdateObservable> {
    return new Observable((observer: Observer<QrRefundStatusUpdateObservable>) => {
      this.httpService.post(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/qr/payments/' + transactionId + '/refunds', refund)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (statusData: any) => {
            if (statusData.refundStatus === QrStatus.refunded) {
              observer.next(new QrRefundStatusUpdateObservable(
                true,
                QrRefundStatusUpdate.convertAPIObjectToRefundStatusUpdate(statusData)
              ));
            } else {
              observer.next(new QrRefundStatusUpdateObservable(false));
            }
            observer.complete();
          },
          error: (err: any) => {
            observer.next(new QrRefundStatusUpdateObservable(false));
            observer.complete();
          }
        });
    });
  }

  // Confirm Refund for cancel
  public confirmRefund(refund: any, transactionId: string): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      this.httpService.post(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/payments/' + transactionId + '/refund/confirm', refund)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (statusData: any) => {
            observer.next(true);
            observer.complete();
          },
          error: (err: any) => {
            observer.next(false);
            observer.complete();
          }
        });
    });
  }

  // Refund QR stage for cancel
  public refundQRPaymentStage(transactionId: string, refundStage: QrRefundStage): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      this.httpService.post(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/payments/' + transactionId + '/refund/stage', refundStage)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (resp) => {
            observer.next(true);
            observer.complete();
          },
          error: (err: any) => {
            observer.next(false);
            observer.complete();
          }
        });
    });
  }

  // Refund and refundstage during cancel
  public refundQrCancel(transactionId: string, refund: Payment): Observable<QrRefundStatusUpdateObservable> {
    let refunded: QrRefundStatusUpdateObservable;
    return new Observable((observer: Observer<any>) => {
      this.refundQRPayment(transactionId, QrRefund.convertPaymentToRefund(refund))
        .pipe(mergeMap((refundResp: QrRefundStatusUpdateObservable) => {
          if (refundResp.status) {
            refunded = refundResp;
            return this.refundQRPaymentStage(
              transactionId,
              QrRefundStage.convertPaymentToStage(refund, refundResp.statusUpdate.refundTransactionId)
            );
          }
          return throwError(() => new Error('Refund Failed'));
        }),
          catchError((err) => {
            return throwError(() => err);
          }))
        .subscribe({
          next: (stageResp: boolean) => {
            if (stageResp) {
              observer.next(refunded);
            } else {
              observer.next(new QrRefundStatusUpdateObservable(false));
            }
            observer.complete();
          },
          error: (err) => {
            observer.next(new QrRefundStatusUpdateObservable(false));
            observer.complete();
          }
        });
    });
  }

  // Create Payment Orchestrator Session
  public createSession(obj: CreatePaymentSessionRequest): Observable<CreatePaymentSessionResponse> {
    return new Observable((observer: Observer<any>) => {
      this.metricsApiService.log(LogLevel.Info, `Attempting payment session creation`);
      this.httpService.post(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/payment/session', obj)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (paymentSessionResponse: any) => {
            this.metricsApiService.log(LogLevel.Info, `Create payment session completed.`);
            this.paymentOrchestratorRetryCounter = 0;
            observer.next(paymentSessionResponse);
            observer.complete();
          },
          error: (err: any) => {
            if (err.status === 429) {
              if (this.paymentOrchestratorRetryCounter < this.bootstrapParams.paymentOrchestratorMaxRetries) {
                this.paymentOrchestratorRetryCounter++;

                this.metricsApiService.log(LogLevel.Info, `Received 429 response from payment orchestrator. Retrying ${this.paymentOrchestratorRetryCounter} of ${this.bootstrapParams.paymentOrchestratorMaxRetries}.`);
                setTimeout(() => {
                  this.createSession(obj).subscribe({
                    next: (resp) => {
                      observer.next(resp);
                      observer.complete();
                    },
                    error: (err: any) => {
                      observer.error(err);
                      observer.complete();
                    }
                  });
                }, this.bootstrapParams.paymentOrchestratorRetryTime);
              } else {
                this.metricsApiService.log(LogLevel.Info, `Received 429 response from payment orchestrator. Max retries exceeded.`);
                this.paymentOrchestratorRetryCounter = 0;
                observer.error('Orchestrator unreachable');
                observer.complete();
              }
            } else {
              this.metricsApiService.log(LogLevel.Info, `Error creating payment session: ${JSON.stringify(err)}`);
              this.paymentOrchestratorRetryCounter = 0;
              observer.error(err);
              observer.complete();
            }
          }
        });
    });
  }

  public verifyPayment(paymentOrchestratorId: string, holdId: string): Observable<PaymentSession> {
    return new Observable((observer: Observer<any>) => {
      // Begin polling payment orchestrator for payment status
      this.metricsApiService.log(LogLevel.Info, `Begin polling payment status for \nHold: ${holdId}`);
      const begin = moment();
      const end = begin.add(this.bootstrapParams.paymentOrchestratorPollingLimit, "milliseconds");

      this.getSessionPaymentStatus(paymentOrchestratorId)
        .subscribe({
          next: (status) => {
            if (!!status && status.paymentStatus === PaymentOrchestratorPaymentStatus.paymentSucceeded) {
            this.stopPolling.next(holdId);
            this.stopPolling.complete();
            observer.next(status);
            observer.complete();
            } else if (!!status && (status.paymentStatus === PaymentOrchestratorPaymentStatus.paymentRefunded || status.paymentStatus === PaymentOrchestratorPaymentStatus.paymentFailed || status.paymentStatus === PaymentOrchestratorPaymentStatus.paymentExpired)) {
              this.metricsApiService.log(LogLevel.Info, `Polling indicated failure with payment. \nStatus: ${status.paymentStatus} \nHold: ${holdId}`);
            this.stopPolling.next(holdId);
            this.stopPolling.complete();
            observer.error('Polling indicated failure with payment');
            observer.complete();
          } else {
            interval(this.bootstrapParams.paymentOrchestratorPollingTime)
              .pipe(
                switchMap(() => this.getSessionPaymentStatus(paymentOrchestratorId)),
                takeUntil(this.stopPolling)
              )
                .subscribe({
                  next: (resp) => {
                    const now = moment();
                    if (!!resp && resp.paymentStatus === PaymentOrchestratorPaymentStatus.paymentSucceeded) {
                  this.stopPolling.next(holdId);
                  this.stopPolling.complete();
                  observer.next(resp);
                  observer.complete();
                    } else if (!!resp && (resp.paymentStatus === PaymentOrchestratorPaymentStatus.paymentRefunded || resp.paymentStatus === PaymentOrchestratorPaymentStatus.paymentFailed || resp.paymentStatus === PaymentOrchestratorPaymentStatus.paymentExpired)) {
                      this.metricsApiService.log(LogLevel.Info, `Polling indicated failure with payment. \nStatus: ${resp.paymentStatus} \nHold: ${holdId}`);
                  this.stopPolling.next(holdId);
                  this.stopPolling.complete();
                  observer.error('Polling indicated failure with payment');
                  observer.complete();
                } else if (now > end) {
                  this.metricsApiService.log(LogLevel.Info, `Polling timed out without receiving a success payment status. \nHold: ${holdId}`);
                  this.stopPolling.next(holdId);
                  this.stopPolling.complete();
                  observer.error('Polling time out');
                  observer.complete();
                }
                  }
              });
          }
          }
        });
    });
  }

  public stageSessionPayment(paymentSessionData: PaymentSession, type: CardFee, examDetails: any[], holdId: string): Observable<any> {
    const paymentRequest: PaymentOrchestratorRequest = new PaymentOrchestratorRequest(
      paymentSessionData.paymentOrchestratorId,
      paymentSessionData.paymentIntentId,
      paymentSessionData.accountId,
      type,
      examDetails,
      paymentSessionData.currency,
      paymentSessionData.cost
    );

    return new Observable((observer: Observer<any>) => {
      this.metricsApiService.log(LogLevel.Info, `Attempting to stage payment. \nHold: ${holdId}`);

      this.stagePayment(paymentRequest).subscribe({
        next: (resp) => {
          this.metricsApiService.log(LogLevel.Info, `Stage payment completed. \nHold: ${holdId}`);
          observer.next(resp);
          observer.complete();
        },
        error: (err) => {
          this.metricsApiService.log(LogLevel.Info, `Error staging payment. \nHold: ${holdId}`);
          observer.error(err);
          observer.complete();
        }
      });
    });
  }

  private getSessionPaymentStatus(paymentOrchestratorId: string): Observable<PaymentSession> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.get(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/payment/session/' + paymentOrchestratorId)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (err) => {
            this.metricsApiService.log(LogLevel.Info, `Received error response from polling payment status. Error Code: ${!!err.status ? err.status : 'Unknown (0)'}`);
              observer.next(null);
              observer.complete();
            }
        });
    });
  }

  private stagePayment(paymentRequest: PaymentOrchestratorRequest): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.post(this.bootstrapParams.PaymentRootApiUrl + 'api/v2/payments/', paymentRequest)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (rNArray) => {
            observer.next(rNArray);
            observer.complete();
          },
          error: (err) => {
            observer.error(err);
            observer.complete();
          }
        });
    });
  }

  // Get whether transaction is associated with a booking
  private transactionUsed(transactionId: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.get(this.bootstrapParams.PaymentRootApiUrl + `api/v1/payments/${transactionId}/status`)
        .subscribe({
          next: (resp) => {
            observer.next(resp);
            observer.complete();
          },
          error: (err) => {
            observer.error(err);
            observer.complete();
          }
        });
    });
  }
}
